import React, { useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import "../../ModuleSubtopic1.css";
import NavigationButtons from "../../components/NavigationButtons";
import { useSubtopicNavigation } from "../../hooks/useSubtopicNavigation";

const ALL_TOC_SUBTOPICS = [
  { name: 'Introduction to Formal Languages and Automata', path: '/toc-m1-s1' },
  { name: 'Alphabets, Strings and Languages', path: '/toc-m1-s2' },
  { name: 'Introduction to Deterministic Finite Automata (DFA)', path: '/toc-m1-s3' },
  { name: 'DFA Definition and Examples', path: '/toc-m1-s4' },
  { name: 'Module Summary', path: '/toc-m1-s5' },
  { name: 'Non-deterministic Finite Automata (NFA)', path: '/toc-m2-s1' },
  { name: 'NFA to DFA Conversion', path: '/toc-m2-s2' },
  { name: 'Epsilon NFA', path: '/toc-m2-s3' },
  { name: 'Minimizing DFA', path: '/toc-m2-s4' },
  { name: 'Module Summary', path: '/toc-m2-s5' },
  { name: 'Introduction to Regular Expressions', path: '/toc-m3-s1' },
  { name: 'Regular Expressions Operations and Properties', path: '/toc-m3-s2' },
  { name: 'Finite Automata and Regular Expressions', path: '/toc-m3-s3' },
  { name: 'Introduction to Regular Grammars', path: '/toc-m3-s4' },
  { name: 'Module Summary', path: '/toc-m3-s5' },
  { name: 'Properties of Regular Languages', path: '/toc-m4-s1' },
  { name: 'Pumping Lemma', path: '/toc-m4-s2' },
  { name: 'Applications of Regular Languages', path: '/toc-m4-s3' },
  { name: 'Module Summary', path: '/toc-m4-s4' },
  { name: 'Introduction to Context-free Grammars (CFG)', path: '/toc-m5-s1' },
  { name: 'Derivations and Parse Trees', path: '/toc-m5-s2' },
  { name: 'Ambiguity in CFG', path: '/toc-m5-s3' },
  { name: 'Simplification of CFG', path: '/toc-m5-s4' },
  { name: 'Module Summary', path: '/toc-m5-s5' },
  { name: 'Introduction to Push-Down Automata (PDA)', path: '/toc-m6-s1' },
  { name: 'PDA Definition and Examples', path: '/toc-m6-s2' },
  { name: 'CFG and PDA Equivalence', path: '/toc-m6-s3' },
  { name: 'Deterministic PDA', path: '/toc-m6-s4' },
  { name: 'Module Summary', path: '/toc-m6-s5' },
  { name: 'Turing Machines', path: '/toc-m7-s1' },
  { name: 'Turing Machine Variants', path: '/toc-m7-s2' },
  { name: 'Decidability and Halting Problem', path: '/toc-m7-s3' },
  { name: 'Module Summary', path: '/toc-m7-s4' }
];

const VideoList = ({ videos, onVideoClick }) => (
  <div className="subtopic-video-list">
    {videos.map((video, index) => (
      <div
        key={index}
        className="subtopic-video-wrapper"
        onClick={() => onVideoClick(video)}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => e.key === "Enter" && onVideoClick(video)}
      >
        <iframe
          src={video}
          allowFullScreen
          title={`Video ${index + 1}`}
          className="subtopic-video-frame"
        ></iframe>
      </div>
    ))}
  </div>
);

const TOCModuleSubtopic = ({ title, videos, takeaways, questions, exampleVideos, subtopics, subjectPath }) => {
  const [expandedVideo, setExpandedVideo] = useState(null);
  const location = useLocation();

  const { subtopics: stateSubtopics = ALL_TOC_SUBTOPICS, subjectPath: stateSubjectPath = '/subject-TOCModules' } = location.state || {};

  const navSubtopics = useMemo(() => {
    return (subtopics && subtopics.length > 0) ? subtopics : (stateSubtopics && stateSubtopics.length > 0 ? stateSubtopics : ALL_TOC_SUBTOPICS);
  }, [subtopics, stateSubtopics]);

  const finalSubjectPath = subjectPath || stateSubjectPath || '/subject-TOCModules';

  const { prevPath, nextPath, handleBack } = useSubtopicNavigation(
    navSubtopics,
    location.pathname,
    finalSubjectPath
  );
  return (
    <div className="subtopic-main">
      <div className="subtopic-container">
        <div className="subtopic-header">{title}</div>

        {videos.length > 0 && (
          <div className="subtopic-section">
            <h2>Videos</h2>
            <VideoList videos={videos} onVideoClick={setExpandedVideo} />
          </div>
        )}

        {takeaways.length > 0 && (
          <div className="subtopic-section">
            <h2>Key Takeaways</h2>
            {takeaways.map((item, index) => (
              <div key={index} className="takeaway-item">
                <p>- {item.text}</p>
                {item.image && <img src={item.image} alt={`Takeaway ${index + 1}`} className="takeaway-image" />}
              </div>
            ))}
          </div>
        )}

        {questions.length > 0 && (
          <div className="subtopic-section">
            <h2>Previous Questions</h2>
            {questions.map((question, index) => (
              <div key={index} className="question-item">
                <p>- {question.text}</p>
                {question.image && <img src={question.image} alt={`Question ${index + 1}`} className="question-image" />}
              </div>
            ))}
          </div>
        )}

        {exampleVideos.length > 0 && (
          <div className="subtopic-section">
            <h2>Example Question Videos</h2>
            <VideoList videos={exampleVideos} onVideoClick={setExpandedVideo} />
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <NavigationButtons
        prevPath={prevPath}
        nextPath={nextPath}
        onBack={handleBack}
        subjectPath={finalSubjectPath}
        subtopics={navSubtopics}
      />

      {/* Video Modal Overlay */}
      {expandedVideo && (
        <div
          className="video-modal-overlay"
          onClick={() => setExpandedVideo(null)}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => e.key === "Escape" && setExpandedVideo(null)}
        >
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="video-modal-close"
              onClick={() => setExpandedVideo(null)}
              aria-label="Close video"
            >
              ✕
            </button>
            <iframe
              src={expandedVideo}
              allowFullScreen
              title="Expanded Video"
              className="video-modal-iframe"
            ></iframe>
          </div>
        </div>
      )}
    </div>
  );
};

export default TOCModuleSubtopic;
